import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Fuel, Gauge, Users, Settings, Phone, Mail } from 'lucide-react';
import { companyInfo } from '../data/mock';

const CarDetailModal = ({ car, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Car images array - use multiple angles if available, otherwise repeat main image
  const images = car?.images || [car?.image, car?.image, car?.image];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentImageIndex(0);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Auto-rotate images
  useEffect(() => {
    if (!isOpen || images.length <= 1) return;
    const interval = setInterval(() => {
      nextImage();
    }, 4000);
    return () => clearInterval(interval);
  }, [isOpen, currentImageIndex, images.length]);

  const nextImage = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevImage = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Car specifications based on category
  const getCarSpecs = () => {
    const specs = {
      'Exotic': { engine: 'V10/V12', hp: '600-800', seats: 2, transmission: 'Automatic DCT' },
      'Luxury': { engine: 'V8 Twin-Turbo', hp: '450-600', seats: 5, transmission: 'Automatic' },
      'SUV': { engine: 'V6/V8', hp: '350-550', seats: 5, transmission: 'Automatic AWD' },
      'Sports': { engine: 'V6/V8', hp: '400-500', seats: 2, transmission: 'Automatic' },
      'Sedan': { engine: 'I4/V6 Turbo', hp: '250-400', seats: 5, transmission: 'Automatic' },
      'Coupe': { engine: 'I4/V6 Turbo', hp: '300-450', seats: 4, transmission: 'Automatic' },
      'Convertible': { engine: 'I4/V6 Turbo', hp: '280-400', seats: 4, transmission: 'Automatic' },
      'Economy': { engine: 'I4', hp: '150-200', seats: 5, transmission: 'Automatic CVT' },
    };
    return specs[car?.category] || specs['Sedan'];
  };

  const specs = getCarSpecs();

  if (!isOpen || !car) return null;

  const scrollToInquiry = () => {
    onClose();
    setTimeout(() => {
      document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm animate-fadeIn" />
      
      {/* Modal Content */}
      <div 
        className="relative w-full max-w-5xl bg-black border-2 border-amber-500/40 rounded-3xl overflow-hidden shadow-2xl shadow-amber-500/20 animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black transition-all duration-300"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col lg:flex-row">
          {/* Image Section - Full showroom image display */}
          <div className="relative lg:w-3/5 h-72 lg:h-auto min-h-[400px] bg-black">
            {/* Car Image - The showroom is already in the generated images */}
            <div className="relative h-full flex items-center justify-center">
              <img
                src={images[currentImageIndex]}
                alt={car.name}
                className={`w-full h-full object-cover transition-all duration-500 ${
                  isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                }`}
              />
            </div>

            {/* Image Navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 border border-amber-500/30 text-white hover:bg-amber-500 hover:text-black transition-all duration-300"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 border border-amber-500/30 text-white hover:bg-amber-500 hover:text-black transition-all duration-300"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Image Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    idx === currentImageIndex 
                      ? 'bg-amber-500 w-6' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Info Section */}
          <div className="lg:w-2/5 p-6 lg:p-8 flex flex-col bg-black">
            {/* Category & Name */}
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-amber-500/20 border border-amber-500 rounded-full text-amber-400 text-xs font-semibold uppercase tracking-wider mb-3">
                {car.category}
              </span>
              <h2 className="text-3xl font-bold text-white">{car.name}</h2>
            </div>

            {/* Price */}
            <div className="mb-6 p-4 bg-amber-500/10 rounded-xl border border-amber-500/40">
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-bold text-amber-400">${car.price}</span>
                <span className="text-amber-200/60">CAD / day</span>
              </div>
              {car.originalPrice && (
                <div className="mt-1">
                  <span className="text-gray-500 line-through text-lg">${car.originalPrice}</span>
                  <span className="ml-2 text-amber-300 text-sm font-medium">
                    Save ${car.originalPrice - car.price}
                  </span>
                </div>
              )}
            </div>

            {/* Specifications */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-3">Specifications</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <Fuel className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="text-xs text-amber-200/50">Engine</p>
                    <p className="text-sm text-white font-medium">{specs.engine}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <Gauge className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="text-xs text-amber-200/50">Horsepower</p>
                    <p className="text-sm text-white font-medium">{specs.hp} HP</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="text-xs text-amber-200/50">Seats</p>
                    <p className="text-sm text-white font-medium">{specs.seats} Passengers</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <Settings className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="text-xs text-amber-200/50">Transmission</p>
                    <p className="text-sm text-white font-medium">{specs.transmission}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Actions */}
            <div className="mt-auto space-y-3">
              <button
                onClick={scrollToInquiry}
                className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-xl hover:shadow-[0_0_30px_rgba(212,160,23,0.5)] transition-all duration-300 transform hover:scale-[1.02]"
              >
                Inquire About This Vehicle
              </button>
              
              <div className="flex gap-3">
                <a
                  href={`tel:${companyInfo.phone.replace(/-/g, '')}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-black border border-amber-500/40 rounded-xl text-white hover:bg-amber-500/10 hover:border-amber-500 transition-all duration-300"
                >
                  <Phone className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">Call Us</span>
                </a>
                <a
                  href={`mailto:${companyInfo.email}?subject=Inquiry about ${car.name}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-black border border-amber-500/40 rounded-xl text-white hover:bg-amber-500/10 hover:border-amber-500 transition-all duration-300"
                >
                  <Mail className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">Email</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetailModal;
